"use client";

import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { CheckIcon } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/shared/lib/utils";

const checkboxVariants = cva("peer shrink-0 border transition-shadow outline-none disabled:cursor-not-allowed", {
  variants: {
    variant: {
      // Admin 스타일 (기존 shadcn 스타일)
      admin: [
        // 기본 스타일
        "size-4 rounded-[4px] shadow-xs",
        // 테마 & 보더
        "border-input dark:bg-input/30",
        // 체크 상태
        "data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground",
        "data-[state=checked]:border-primary dark:data-[state=checked]:bg-primary",
        // 포커스 상태
        "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
        // 비활성화 상태
        "disabled:cursor-not-allowed disabled:opacity-50",
        // 에러 상태
        "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
      ],
      // Public 스타일 (프로젝트 커스텀 스타일)
      public: [
        // 기본 스타일
        "tb:size-6 size-5 rounded-[6px]",
        // 테마 & 보더
        "border-gray-400 bg-white",
        // 체크 상태
        "data-[state=checked]:bg-primary-500 data-[state=checked]:text-white",
        "data-[state=checked]:border-primary-500",
        // 포커스 상태
        "focus-visible:border-primary-500 focus-visible:ring-primary-500/20 focus-visible:ring-2",
        // 에러 상태
        "aria-invalid:border-red-error aria-invalid:ring-red-error/20",
        // 비활성화 상태
        "disabled:bg-gray-200 disabled:border-gray-300 disabled:text-gray-400 disabled:data-[state=checked]:bg-gray-200 disabled:data-[state=checked]:text-gray-300 disabled:data-[state=checked]:border-gray-300",
      ],
    },
  },
  defaultVariants: {
    variant: "admin",
  },
});

export interface CheckboxProps
  extends React.ComponentProps<typeof CheckboxPrimitive.Root>,
    VariantProps<typeof checkboxVariants> {}

function Checkbox({ className, variant, ...props }: CheckboxProps) {
  return (
    <CheckboxPrimitive.Root data-slot="checkbox" className={cn(checkboxVariants({ variant, className }))} {...props}>
      <CheckboxPrimitive.Indicator
        data-slot="checkbox-indicator"
        className="flex items-center justify-center text-current transition-none"
      >
        <CheckIcon className={variant === "public" ? "tb:size-4 size-[14px]" : "size-3.5"} />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}

export { Checkbox };
